import { isDefined } from '../../../../utils/variables';
import { clamp, Point2d, Point3d } from '../../../utilities/math-helpers';

type LWPolylineVertex = {
  x: number;
  y: number;
  z: number;
  bulge: number;
};

type ArcEntity = {
  center: Point3d;
  radius: number;
  /**
   * Start angle in radians.
   */
  startAngle: number;
  /**
   * End angle in radians.
   */
  endAngle: number;
  extrusionDirection?: Point3d;
};

function polar(center: Point2d, angle: number, distance: number): Point2d {
  return {
    x: center.x + distance * Math.cos(angle),
    y: center.y + distance * Math.sin(angle),
  };
}

function angle(p1: Point2d, p2: Point2d): number {
  return Math.atan2(p2.y - p1.y, p2.x - p1.x);
}

function distance(p1: Point2d, p2: Point2d): number {
  return Math.hypot(p2.x - p1.x, p2.y - p1.y);
}

function bulgeCenter(p1: Point2d, p2: Point2d, bulge: number): Point2d {
  const theta = angle(p1, p2) + (Math.PI / 2 - 2 * Math.atan(bulge));
  const dist = (distance(p1, p2) * (1 + bulge * bulge)) / (4 * bulge);
  return polar(p1, theta, dist);
}

/**
 * Gets the number of steps for the arc.
 */
export function getNumberOfSteps(radius: number, angleLength: number): number {
  // Minimum segment length to avoid generating an excessive amount of vertices.
  const minSegmentLength = 0.04;
  // Maximum segment length to handle situations where the radius is very large.
  const maxSegmentLength = 3;
  // Max % of the whole circles circumference that the segment should stretch over in order to represent meaningful curvature.
  const desiredSegmentLength = 0.04 * radius;

  const segmentLength = Math.min(
    Math.max(desiredSegmentLength, minSegmentLength),
    maxSegmentLength,
  );

  const circumference = 2 * Math.PI * radius;
  const arcLength = circumference * (angleLength / (2 * Math.PI));
  return clamp(Math.ceil(arcLength / segmentLength), 2, 1000);
}

/**
 * Takes a polyline like [v1,v2,v3,v4] and converts it to [v1,v2,v2,v3,v3,v4].
 */
export function polylineToSegments(polyline: any[]): any[] {
  const segments: any[] = [];

  if (polyline.length < 2) {
    return segments;
  }

  for (let i = 0; i < polyline.length - 1; i++) {
    segments.push(polyline[i], polyline[i + 1]);
  }

  return segments;
}

/**
 * Calculates the arc points around the center between the start and end angle.
 */
function calculateArcPoints(
  center: Point2d,
  radius: number,
  startAngle: number,
  endAngle: number,
  direction: number,
  z = 0,
): Point3d[] {
  // Normalize angles to range [0, 2π).
  if (startAngle !== 2 * Math.PI) {
    startAngle = (startAngle + 2 * Math.PI) % (2 * Math.PI);
  }

  if (endAngle !== 2 * Math.PI) {
    endAngle = (endAngle + 2 * Math.PI) % (2 * Math.PI);
  }

  // Adjust angles for direction.
  if (direction > 0 && startAngle > endAngle) {
    endAngle += 2 * Math.PI;
  } else if (direction < 0 && startAngle < endAngle) {
    startAngle += 2 * Math.PI;
  }

  // Calculate the total angle and the step between each point.
  const totalAngle = endAngle - startAngle;
  const stepCount = getNumberOfSteps(radius, Math.abs(totalAngle));
  const stepAngle = totalAngle / (stepCount - 1);

  // Generate the points.
  const points: Point3d[] = [];
  for (let i = 0; i < stepCount; i++) {
    const currentAngle = startAngle + stepAngle * i;
    const point = polar(center, currentAngle, radius);
    points.push({ x: point.x, y: point.y, z: z });
  }

  return points;
}

export function getArcVerticesFromBulge(v1: LWPolylineVertex, v2: LWPolylineVertex) {
  if (v1.bulge === 0 || !isDefined(v1.bulge)) {
    return [
      { x: v1.x, y: v1.y },
      { x: v2.x, y: v2.y },
    ];
  }

  // Calculate center and radius of the arc.
  const center = bulgeCenter(v1, v2, v1.bulge);
  const radius = distance(v1, center);

  // Calculate the start and end angles.
  const startAngle = angle(center, v1);
  const endAngle = angle(center, v2);

  return calculateArcPoints(center, radius, startAngle, endAngle, Math.sign(v1.bulge));
}

export function getArcVerticesFromEntity(entity: ArcEntity): Point3d[] {
  const inverted = (entity.extrusionDirection?.z ?? 1) < 0;

  // If inverted, flip the center x-coordinate and switch start and end angles.
  const center = {
    x: inverted ? -entity.center.x : entity.center.x,
    y: entity.center.y,
    z: entity.center.z ?? 0,
  };

  let startAngle = entity.startAngle;
  let endAngle = entity.endAngle;

  // Flip the arcs across the center line and then adjust them by an additional 180 degrees if inverted.
  if (inverted) {
    startAngle = 3 * Math.PI - entity.endAngle;
    endAngle = 3 * Math.PI - entity.startAngle;
  }

  return calculateArcPoints(center, entity.radius, startAngle, endAngle, 1, center.z);
}
